/**
 * Shared helper for Edge Function responses.
 *
 * Edge Functions return a `{ data, error }` envelope (see
 * `packages/db/supabase/functions/_shared/response.ts`). When called via
 * `supabase.functions.invoke()`, supabase-js gives the SDK helper its own
 * outer `{ data, error }` shape, where the inner `data` is the entire
 * envelope. `unwrapEdge` peels that envelope so SDK helpers can return the
 * real payload to callers.
 *
 * Use this for the success path only. Transport-level errors (network,
 * 4xx/5xx from the function) still go through the existing
 * `if (error) throw error` branch in each helper.
 */

export interface EdgeEnvelope<T> {
  data: T | null;
  error: { code: string; message: string; details?: unknown } | null;
}

export function unwrapEdge<T>(raw: unknown): T {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Edge function returned no body');
  }
  const env = raw as EdgeEnvelope<T>;
  if (env.error) {
    const e = new Error(env.error.message) as Error & { code?: string };
    e.code = env.error.code;
    throw e;
  }
  if (env.data == null) {
    throw new Error('Edge function returned empty data');
  }
  return env.data;
}

/**
 * Best-effort parse of the JSON body from a transport-level
 * `supabase.functions.invoke()` failure.
 *
 * supabase-js throws `FunctionsHttpError` for non-2xx responses and sets
 * `error.context` to the raw `Response`. Several call sites historically read
 * `error.context.response` — which is ALWAYS `undefined`, so every edge error
 * silently collapsed to each helper's generic fallback code/message (e.g. the
 * marketing lead form showed the generic "something went wrong on our end"
 * instead of the friendly rate-limit copy). This helper reads `context` as the
 * `Response` directly and still tolerates the older `{ response }` shape.
 *
 * Returns the parsed `{ error?, message? }` envelope, or `null` when the body
 * is absent or not JSON, so each caller keeps its own fallback logic.
 */
export async function readEdgeErrorBody(
  error: unknown,
): Promise<{ error?: { code?: string; message?: string }; message?: string } | null> {
  const response = toEdgeResponse((error as { context?: unknown } | null)?.context);
  if (!response) return null;
  try {
    return (await response.clone().json()) as {
      error?: { code?: string; message?: string };
      message?: string;
    };
  } catch {
    return null;
  }
}

function toEdgeResponse(context: unknown): Response | null {
  if (!context || typeof context !== 'object') return null;
  const maybe = context as { clone?: unknown; json?: unknown; response?: unknown };
  // supabase-js sets `error.context` to the Response itself.
  if (typeof maybe.clone === 'function' && typeof maybe.json === 'function') {
    return context as Response;
  }
  // Defensive: tolerate a `{ response: Response }` wrapper.
  const nested = maybe.response as { clone?: unknown } | undefined;
  if (nested && typeof nested.clone === 'function') return nested as Response;
  return null;
}
